import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft, CalendarClock, ChevronDown, Download, FileText, Loader2, Lock, Mail, MailOpen, MessageSquare, Paperclip,
  RefreshCw, Send, Sparkles, Star, Tag, UserRound, X,
} from 'lucide-react';
import { api } from '../../../lib/api';
import { useStore } from '../../../store/useStore';
import { AuroraLogo } from '../../office/icons';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { guardarBlob } from '../respuestas/datos';
import { mensajeDe } from '../responder/utilidades';
import SelectorPersona, { Avatar } from './SelectorPersona';
import {
  ESTADOS, NOMBRE_ESTADO, NOMBRE_PRIORIDAD, PRIORIDADES, SENTIMIENTOS, aInputLocal, deInputLocal, fechaHora, haceCuanto, textoPlazo,
} from './constantes';

/*
 * Un caso del buzón: propiedades (estado, prioridad, categoría, asignado, plazo, etiquetas), lo que
 * propone Aurora IA, el mensaje tal como llegó, el historial y la caja para responder o dejar una nota.
 *
 *   <DetalleCaso rid onCerrar onCambio />
 *
 * `onCambio(fila | null)` avisa a la bandeja para que su lista no se quede atrás (null = ya no lo ves).
 * Qué se puede tocar lo dice el servidor en `permisos` (ver buzon.permisos).
 */

export default function DetalleCaso({ rid, onCerrar, onCambio, volver = false }) {
  const [estado, setEstado] = useState({ cargando: true, datos: null, error: null });
  const [guardando, setGuardando] = useState(null);
  const [reasignar, setReasignar] = useState(null); // persona elegida a la que se va a pasar el caso
  const yo = useStore((st) => st.user);
  const onCambioRef = useRef(onCambio);
  onCambioRef.current = onCambio;

  const cargar = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setEstado((e) => ({ ...e, cargando: true, error: null }));
    try {
      const { data } = await api.get(`/buzon/casos/${rid}`);
      setEstado({ cargando: false, datos: data, error: null });
      onCambioRef.current?.(data.caso);
    } catch (err) {
      setEstado({ cargando: false, datos: null, error: mensajeDe(err, 'No se pudo abrir el caso') });
    }
  }, [rid]);

  useEffect(() => { cargar(); }, [cargar]);

  // Otra persona puede estar trabajando el mismo caso: se refresca solo cada 30 s si nadie escribe
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === 'visible' && !document.querySelector('.bz-redactar textarea:focus')) cargar({ silencioso: true });
    }, 30000);
    return () => clearInterval(t);
  }, [cargar]);

  const recibir = (data, mensajeOk) => {
    if (data?.sin_acceso) {
      toast.success('Caso reasignado. Ya no está en tu bandeja.');
      onCambio?.(null);
      onCerrar?.();
      return;
    }
    setEstado({ cargando: false, datos: data, error: null });
    onCambio?.(data.caso);
    if (mensajeOk) toast.success(mensajeOk);
  };

  const cambiar = async (cambios, mensajeOk) => {
    setGuardando(Object.keys(cambios)[0]);
    try {
      const { data } = await api.patch(`/buzon/casos/${rid}`, cambios);
      recibir(data, mensajeOk);
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo guardar el cambio'));
    } finally {
      setGuardando(null);
    }
  };

  const marcar = async (marcas) => {
    try {
      await api.put(`/buzon/casos/${rid}/marcas`, marcas);
      setEstado((e) => (e.datos ? { ...e, datos: { ...e.datos, caso: { ...e.datos.caso, ...('destacado' in marcas ? { destacado: marcas.destacado } : {}), ...('leido' in marcas ? { leido: marcas.leido } : {}) } } } : e));
      onCambio?.({ ...estado.datos.caso, ...marcas });
      if (marcas.leido === false) onCerrar?.();
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo marcar'));
    }
  };

  if (estado.cargando && !estado.datos) {
    return <div className="bz-detalle bz-centro"><Loader2 className="h-7 w-7 od-spin" aria-label="Cargando caso" /></div>;
  }
  if (estado.error) {
    return (
      <div className="bz-detalle bz-centro">
        <p>{estado.error}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => cargar()}>Reintentar</Button>
          {onCerrar && <Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
        </div>
      </div>
    );
  }
  const { caso, formulario, respuestas, eventos, permisos } = estado.datos;
  const categorias = formulario.categorias || [];
  const abierto = ESTADOS.find((e) => e.id === caso.estado)?.abierto;

  const elegirPersona = (p) => {
    if ((p?.id || null) === (caso.asignado?.id || null)) return;
    if (caso.asignado && p) setReasignar(p); // pasar un caso de alguien a otra persona pide (opcional) el motivo
    else cambiar({ asignado_id: p?.id || null }, p ? `Asignado a ${p.nombre}` : 'Caso sin asignar');
  };

  return (
    <article className="bz-detalle" aria-label={`Caso ${caso.folio}`}>
      <header className="bz-detalle-cabecera">
        {onCerrar && (
          <button type="button" className="od-tool-btn" onClick={onCerrar} aria-label={volver ? 'Volver a la lista' : 'Cerrar el caso'}>
            {volver ? <ArrowLeft className="h-5 w-5" /> : <X className="h-5 w-5" />}
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="bz-detalle-folio">
            <span>{caso.folio}</span>
            <span className={`bz-estado is-${caso.estado}`}>{NOMBRE_ESTADO[caso.estado]}</span>
            {caso.vencido && <span className="bz-chip is-peligro">Vencido</span>}
          </div>
          <p className="bz-detalle-sub">
            {formulario.titulo} · recibido {haceCuanto(caso.creado_en)}
            {caso.contacto ? ` · ${caso.contacto}` : ' · anónimo'}
          </p>
        </div>
        <button
          type="button"
          className={`od-tool-btn ${caso.destacado ? 'bz-destacado' : ''}`}
          onClick={() => marcar({ destacado: !caso.destacado })}
          aria-pressed={!!caso.destacado}
          aria-label={caso.destacado ? 'Quitar de destacados' : 'Destacar'}
          title={caso.destacado ? 'Quitar de destacados' : 'Destacar'}
        >
          <Star className="h-5 w-5" />
        </button>
        <button type="button" className="od-tool-btn" onClick={() => marcar({ leido: false })} aria-label="Marcar como no leído" title="Marcar como no leído">
          <Mail className="h-5 w-5" />
        </button>
        <button type="button" className="od-tool-btn" onClick={() => cargar()} aria-label="Actualizar" title="Actualizar">
          <RefreshCw className={`h-5 w-5 ${estado.cargando ? 'od-spin' : ''}`} />
        </button>
      </header>

      <div className="bz-detalle-cuerpo od-scroll">
        {/* ---------- Propiedades ---------- */}
        <section className="bz-props" aria-label="Propiedades del caso">
          <Propiedad etiqueta="Estado">
            <select
              className="bz-select"
              value={caso.estado}
              disabled={!permisos.trabajar || guardando === 'estado'}
              onChange={(e) => cambiar({ estado: e.target.value }, `Estado: ${NOMBRE_ESTADO[e.target.value]}`)}
              aria-label="Estado"
            >
              {ESTADOS.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </select>
          </Propiedad>
          <Propiedad etiqueta="Prioridad">
            <select
              className={`bz-select bz-prio is-${caso.prioridad}`}
              value={caso.prioridad}
              disabled={!permisos.trabajar || guardando === 'prioridad'}
              onChange={(e) => cambiar({ prioridad: e.target.value })}
              aria-label="Prioridad"
            >
              {PRIORIDADES.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </Propiedad>
          <Propiedad etiqueta="Categoría">
            <select
              className="bz-select"
              value={caso.categoria || ''}
              disabled={!permisos.trabajar || guardando === 'categoria'}
              onChange={(e) => cambiar({ categoria: e.target.value || null })}
              aria-label="Categoría"
            >
              <option value="">Sin categoría</option>
              {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
            {caso.categoria_origen === 'aurora' && <span className="bz-origen" title="La eligió Aurora IA"><AuroraLogo size={13} /></span>}
          </Propiedad>
          <Propiedad etiqueta="Asignado a">
            {permisos.reasignar && (permisos.gestionar || caso.asignado) ? (
              <SelectorPersona formId={formulario.id} valor={caso.asignado} onElegir={elegirPersona} permitirNadie={permisos.gestionar}>
                <button type="button" className="bz-persona-boton" disabled={guardando === 'asignado_id'}>
                  {caso.asignado ? <Avatar persona={caso.asignado} tam={22} /> : <UserRound className="h-4 w-4" aria-hidden="true" />}
                  <span className="truncate">{caso.asignado?.nombre || 'Sin asignar'}</span>
                  <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
                </button>
              </SelectorPersona>
            ) : (
              <span className="bz-persona-fija">
                {caso.asignado ? <Avatar persona={caso.asignado} tam={22} /> : null}
                {caso.asignado?.nombre || 'Sin asignar'}
              </span>
            )}
            {/* Un caso de mi equipo: cualquiera de sus miembros se lo pasa a sí mismo */}
            {permisos.tomar && yo && (
              <Button
                size="sm"
                variant="outline"
                className="bz-tomar"
                disabled={guardando === 'asignado_id'}
                onClick={() => cambiar({ asignado_id: yo.id, motivo: `Lo tomó del equipo ${caso.asignado?.nombre || ''}`.trim() }, 'El caso es tuyo')}
                data-testid="caso-tomar"
              >
                Tomar el caso
              </Button>
            )}
          </Propiedad>
          <Propiedad etiqueta="Plazo">
            {permisos.plazo ? (
              <input
                type="datetime-local"
                className="bz-select"
                value={aInputLocal(caso.vence_en)}
                onChange={(e) => cambiar({ vence_en: deInputLocal(e.target.value) }, 'Plazo actualizado')}
                aria-label="Plazo para resolver"
              />
            ) : (
              <span className={caso.vencido ? 'bz-texto-peligro' : ''}>{textoPlazo(caso.vence_en, abierto)}</span>
            )}
            {permisos.plazo && caso.vence_en && <span className={`bz-plazo-nota ${caso.vencido ? 'bz-texto-peligro' : ''}`}>{textoPlazo(caso.vence_en, abierto)}</span>}
          </Propiedad>
          <Propiedad etiqueta="Etiquetas" ancho>
            <Etiquetas valor={caso.etiquetas} editable={permisos.trabajar} onCambiar={(etiquetas) => cambiar({ etiquetas })} />
          </Propiedad>
        </section>

        {/* ---------- Aurora IA ---------- */}
        <PanelAurora rid={rid} caso={caso} categorias={categorias} puede={permisos.trabajar} onAplicar={cambiar} onRecargar={() => cargar({ silencioso: true })} />

        {/* ---------- El mensaje, tal como llegó ---------- */}
        <section className="bz-tarjeta" aria-label="Mensaje recibido">
          <h3 className="bz-tarjeta-titulo"><FileText className="h-4 w-4" aria-hidden="true" /> Lo que envió</h3>
          <dl className="bz-respuestas">
            {respuestas.map((r) => (r.tipo === 'seccion' ? (
              <div key={r.id} className="bz-respuesta-seccion">{r.titulo}</div>
            ) : (
              <div key={r.id} className="bz-respuesta">
                <dt>{r.titulo}</dt>
                <dd>
                  {r.archivos?.length ? (
                    <span className="flex flex-wrap gap-2">
                      {r.archivos.map((a) => <Adjunto key={a.id} rid={rid} archivo={a} />)}
                    </span>
                  ) : r.texto ? r.texto : <span className="bz-vacio">Sin respuesta</span>}
                </dd>
              </div>
            )))}
          </dl>
        </section>

        {/* ---------- Historial y conversación ---------- */}
        <section className="bz-tarjeta" aria-label="Historial del caso">
          <h3 className="bz-tarjeta-titulo"><MessageSquare className="h-4 w-4" aria-hidden="true" /> Historial</h3>
          <ol className="bz-historial">
            {eventos.map((e) => <Evento key={e.id} evento={e} categorias={categorias} />)}
          </ol>
        </section>

        {(permisos.trabajar || permisos.comentar) && (
          <Redactar rid={rid} caso={caso} permisos={permisos} seguimiento={formulario.seguimiento_publico} onListo={recibir} onNota={() => cargar({ silencioso: true })} />
        )}
      </div>

      <DialogoReasignar
        persona={reasignar}
        actual={caso.asignado}
        onCancelar={() => setReasignar(null)}
        onConfirmar={(motivo) => {
          const p = reasignar;
          setReasignar(null);
          cambiar({ asignado_id: p.id, motivo }, `Caso pasado a ${p.nombre}`);
        }}
      />
    </article>
  );
}

function Propiedad({ etiqueta, children, ancho }) {
  return (
    <div className={`bz-prop ${ancho ? 'is-ancha' : ''}`}>
      <span className="bz-prop-etiqueta">{etiqueta}</span>
      <div className="bz-prop-valor">{children}</div>
    </div>
  );
}

function Etiquetas({ valor = [], editable, onCambiar }) {
  const [texto, setTexto] = useState('');
  const agregar = () => {
    const t = texto.trim();
    if (!t) return;
    setTexto('');
    if (!valor.some((v) => v.toLowerCase() === t.toLowerCase())) onCambiar([...valor, t]);
  };
  return (
    <div className="bz-etiquetas">
      {valor.map((e) => (
        <span key={e} className="bz-chip">
          <Tag className="h-3 w-3" aria-hidden="true" /> {e}
          {editable && (
            <button type="button" onClick={() => onCambiar(valor.filter((x) => x !== e))} aria-label={`Quitar etiqueta ${e}`}>
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      {editable && valor.length < 10 && (
        <input
          className="bz-etiqueta-nueva"
          value={texto}
          maxLength={30}
          placeholder="Añadir etiqueta"
          aria-label="Añadir etiqueta"
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); agregar(); }
          }}
          onBlur={agregar}
        />
      )}
      {!editable && !valor.length && <span className="bz-vacio">Sin etiquetas</span>}
    </div>
  );
}

function PanelAurora({ rid, caso, categorias, puede, onAplicar, onRecargar }) {
  const [analizando, setAnalizando] = useState(false);
  const ia = caso.ia;
  const sugerida = ia?.categoria && ia.categoria !== caso.categoria ? categorias.find((c) => c.id === ia.categoria) : null;
  const prioridad = ia?.prioridad && ia.prioridad !== caso.prioridad ? ia.prioridad : null;
  const etiquetasNuevas = (ia?.etiquetas || []).filter((e) => !(caso.etiquetas || []).some((x) => x.toLowerCase() === e.toLowerCase()));

  const analizar = async () => {
    setAnalizando(true);
    try {
      await api.post(`/buzon/casos/${rid}/aurora`, { accion: 'triaje' }, { timeout: 90000 });
      onRecargar();
    } catch (err) {
      toast.error(mensajeDe(err, 'Aurora IA no pudo analizar el caso'));
    } finally {
      setAnalizando(false);
    }
  };

  if (!ia && !puede) return null;
  return (
    <section className="bz-tarjeta bz-aurora" aria-label="Aurora IA">
      <h3 className="bz-tarjeta-titulo">
        <AuroraLogo size={16} /> Aurora IA
        {puede && (
          <button type="button" className="bz-boton-texto ml-auto" onClick={analizar} disabled={analizando}>
            {analizando ? <Loader2 className="h-4 w-4 od-spin" /> : <RefreshCw className="h-4 w-4" />}
            {ia ? 'Volver a analizar' : 'Analizar caso'}
          </button>
        )}
      </h3>
      {!ia ? (
        <p className="bz-texto-2">Aurora resume el caso, sugiere categoría, prioridad y etiquetas y propone qué hacer.</p>
      ) : (
        <>
          {ia.resumen && <p className="bz-aurora-resumen">{ia.resumen}</p>}
          <div className="bz-aurora-datos">
            {ia.sentimiento && <span className={`bz-chip is-sent-${ia.sentimiento}`}>Tono: {SENTIMIENTOS[ia.sentimiento]}</span>}
            {ia.prioridad && <span className="bz-chip" title={ia.motivo_prioridad || ''}>Prioridad sugerida: {NOMBRE_PRIORIDAD[ia.prioridad]}</span>}
          </div>
          {ia.motivo_prioridad && <p className="bz-texto-2 text-[13px]">{ia.motivo_prioridad}</p>}
          {ia.accion_sugerida && (
            <p className="bz-aurora-accion"><Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" /> {ia.accion_sugerida}</p>
          )}
          {puede && (sugerida || prioridad || etiquetasNuevas.length > 0) && (
            <div className="bz-aurora-aplicar">
              {sugerida && <button type="button" className="bz-boton-borde" onClick={() => onAplicar({ categoria: sugerida.id })}>Usar categoría «{sugerida.nombre}»</button>}
              {prioridad && <button type="button" className="bz-boton-borde" onClick={() => onAplicar({ prioridad })}>Poner prioridad {NOMBRE_PRIORIDAD[prioridad].toLowerCase()}</button>}
              {etiquetasNuevas.length > 0 && (
                <button type="button" className="bz-boton-borde" onClick={() => onAplicar({ etiquetas: [...(caso.etiquetas || []), ...etiquetasNuevas] })}>
                  Añadir etiquetas: {etiquetasNuevas.join(', ')}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Adjunto({ rid, archivo }) {
  const [bajando, setBajando] = useState(false);
  const bajar = async () => {
    setBajando(true);
    try {
      const res = await api.get(`/buzon/casos/${rid}/archivos/${archivo.id}`, { responseType: 'blob' });
      guardarBlob(res.data, archivo.nombre || 'archivo');
    } catch (err) {
      toast.error('No se pudo descargar el archivo');
    } finally {
      setBajando(false);
    }
  };
  return (
    <button type="button" className="bz-adjunto" onClick={bajar} disabled={bajando} title="Descargar">
      {bajando ? <Loader2 className="h-4 w-4 od-spin" /> : <Paperclip className="h-4 w-4" aria-hidden="true" />}
      <span className="truncate">{archivo.nombre}</span>
      <Download className="h-4 w-4 shrink-0" aria-hidden="true" />
    </button>
  );
}

const quien = (autor, defecto = 'El sistema') => autor?.nombre || defecto;

function Evento({ evento: e, categorias }) {
  const d = e.datos || {};
  const cat = (id) => categorias.find((c) => c.id === id)?.nombre || 'sin categoría';
  if (['nota', 'respuesta', 'ciudadano'].includes(e.tipo)) {
    const clase = e.tipo === 'nota' ? 'is-nota' : e.tipo === 'respuesta' ? 'is-respuesta' : 'is-ciudadano';
    const titulo = e.tipo === 'nota' ? `${quien(e.autor)} · nota interna`
      : e.tipo === 'respuesta' ? `${quien(e.autor)} respondió a quien lo envió`
        : 'Quien lo envió escribió';
    return (
      <li className={`bz-burbuja ${clase}`}>
        <div className="bz-burbuja-cabecera">
          {e.tipo === 'nota' ? <Lock className="h-3.5 w-3.5" aria-hidden="true" /> : e.tipo === 'respuesta' ? <Send className="h-3.5 w-3.5" aria-hidden="true" /> : <MailOpen className="h-3.5 w-3.5" aria-hidden="true" />}
          <span className="flex-1">{titulo}</span>
          <time dateTime={e.creado_en} title={fechaHora(e.creado_en)}>{haceCuanto(e.creado_en)}</time>
        </div>
        <p className="bz-burbuja-texto">{e.texto}</p>
        {e.tipo === 'respuesta' && (
          <p className="bz-burbuja-pie">
            {d.correo_enviado ? `Enviada por correo a ${d.para}` : d.error ? `No salió por correo: ${d.error}` : 'Visible en su página de seguimiento'}
          </p>
        )}
      </li>
    );
  }
  let texto;
  switch (e.tipo) {
    case 'creado': texto = d.previo ? 'Caso creado al activar el buzón' : 'Caso recibido'; break;
    case 'asignado': texto = `${quien(e.autor, 'La regla del buzón')} asignó el caso a ${d.a?.nombre}`; break;
    case 'reasignado': texto = `${quien(e.autor, 'La regla del buzón')} pasó el caso de ${d.de?.nombre} a ${d.a?.nombre}`; break;
    case 'desasignado': texto = `${quien(e.autor)} dejó el caso sin asignar`; break;
    case 'estado': texto = d.reabierto ? `Se reabrió (${NOMBRE_ESTADO[d.a]}) porque quien lo envió volvió a escribir` : `${quien(e.autor)} cambió el estado a ${NOMBRE_ESTADO[d.a] || d.a}`; break;
    case 'prioridad': texto = `${quien(e.autor)} cambió la prioridad a ${NOMBRE_PRIORIDAD[d.a] || d.a}`; break;
    case 'categoria': texto = `${quien(e.autor)} cambió la categoría a ${cat(d.a)}`; break;
    case 'etiquetas': texto = `${quien(e.autor)} ${d.a?.length ? `dejó las etiquetas: ${d.a.join(', ')}` : 'quitó las etiquetas'}`; break;
    case 'vencimiento': texto = `${quien(e.autor)} ${d.a ? `fijó el plazo al ${fechaHora(d.a)}` : 'quitó el plazo'}`; break;
    case 'ia': texto = `Aurora IA clasificó el caso${d.categoria ? ` como ${cat(d.categoria)}` : ''}${d.prioridad ? `, prioridad ${NOMBRE_PRIORIDAD[d.prioridad]?.toLowerCase()}` : ''}`; break;
    case 'vencido': texto = 'Venció el plazo para resolverlo'; break;
    default: texto = e.tipo;
  }
  return (
    <li className={`bz-evento is-${e.tipo}`}>
      <span className="bz-evento-punto" aria-hidden="true">
        {e.tipo === 'ia' ? <AuroraLogo size={12} mono /> : e.tipo === 'vencido' ? <CalendarClock className="h-3 w-3" /> : null}
      </span>
      <span className="flex-1">
        {texto}
        {d.motivo && e.tipo !== 'asignado' && <span className="bz-evento-motivo"> — «{d.motivo}»</span>}
      </span>
      <time dateTime={e.creado_en} title={fechaHora(e.creado_en)}>{haceCuanto(e.creado_en)}</time>
    </li>
  );
}

function Redactar({ rid, caso, permisos, seguimiento, onListo, onNota }) {
  const puedeResponder = permisos.trabajar;
  const [modo, setModo] = useState(puedeResponder ? 'respuesta' : 'nota');
  const [textos, setTextos] = useState({ respuesta: '', nota: '' });
  const [conCorreo, setConCorreo] = useState(true);
  const [estadoFinal, setEstadoFinal] = useState('');
  const [instrucciones, setInstrucciones] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [redactando, setRedactando] = useState(false);
  const texto = textos[modo];
  const setTexto = (v) => setTextos((t) => ({ ...t, [modo]: v }));

  const borrador = async () => {
    setRedactando(true);
    try {
      const { data } = await api.post(`/buzon/casos/${rid}/aurora`, { accion: 'borrador', instrucciones }, { timeout: 90000 });
      setTextos((t) => ({ ...t, respuesta: data.texto || '' }));
    } catch (err) {
      toast.error(mensajeDe(err, 'Aurora IA no pudo redactar la respuesta'));
    } finally {
      setRedactando(false);
    }
  };

  const enviar = async () => {
    if (!texto.trim()) return;
    setEnviando(true);
    try {
      if (modo === 'nota') {
        await api.post(`/buzon/casos/${rid}/notas`, { texto });
        setTextos((t) => ({ ...t, nota: '' }));
        onNota();
      } else {
        const { data } = await api.post(`/buzon/casos/${rid}/responder`, { texto, estado: estadoFinal || null, enviar_correo: conCorreo });
        setTextos((t) => ({ ...t, respuesta: '' }));
        setEstadoFinal('');
        const c = data.correo || {};
        onListo(data, c.enviado ? 'Respuesta enviada por correo' : c.error ? null : 'Respuesta guardada en el caso');
        if (c.error) toast.warning(`Respuesta guardada, pero ${c.error.toLowerCase()}`);
      }
    } catch (err) {
      toast.error(mensajeDe(err, modo === 'nota' ? 'No se pudo guardar la nota' : 'No se pudo enviar la respuesta'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className={`bz-redactar is-${modo}`} aria-label="Responder o dejar una nota">
      <div className="bz-redactar-pestanas" role="tablist">
        {puedeResponder && (
          <button type="button" role="tab" aria-selected={modo === 'respuesta'} className={modo === 'respuesta' ? 'is-activa' : ''} onClick={() => setModo('respuesta')}>
            <Send className="h-4 w-4" aria-hidden="true" /> Responder a quien lo envió
          </button>
        )}
        <button type="button" role="tab" aria-selected={modo === 'nota'} className={modo === 'nota' ? 'is-activa' : ''} onClick={() => setModo('nota')}>
          <Lock className="h-4 w-4" aria-hidden="true" /> Nota interna
        </button>
      </div>
      {modo === 'respuesta' && (
        <p className="bz-redactar-ayuda">
          {caso.contacto
            ? `Le llegará por correo a ${caso.contacto}${seguimiento ? ' y la verá en su página de seguimiento' : ''}.`
            : seguimiento
              ? 'Envió el caso sin correo: la verá en su página de seguimiento (con el enlace que recibió al enviar).'
              : 'Envió el caso sin correo y el buzón no tiene seguimiento en línea: la respuesta solo quedará en el historial.'}
        </p>
      )}
      {modo === 'nota' && <p className="bz-redactar-ayuda">Solo la ve el equipo. Avisa a quien tiene el caso y a quienes ya comentaron.</p>}
      <textarea
        rows={modo === 'respuesta' ? 6 : 3}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder={modo === 'respuesta' ? 'Escribe la respuesta…' : 'Escribe una nota para el equipo…'}
        aria-label={modo === 'respuesta' ? 'Respuesta' : 'Nota interna'}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) enviar(); }}
      />
      {modo === 'respuesta' && (
        <div className="bz-redactar-aurora">
          <input
            value={instrucciones}
            onChange={(e) => setInstrucciones(e.target.value)}
            placeholder="Indicaciones para Aurora (opcional): «dile que el jueves pasa el supervisor»"
            aria-label="Indicaciones para Aurora IA"
          />
          <button type="button" className="bz-boton-aurora" onClick={borrador} disabled={redactando}>
            {redactando ? <Loader2 className="h-4 w-4 od-spin" /> : <AuroraLogo size={15} mono />}
            {texto ? 'Reescribir con Aurora' : 'Redactar con Aurora'}
          </button>
        </div>
      )}
      <div className="bz-redactar-pie">
        {modo === 'respuesta' && caso.contacto && (
          <label className="bz-check">
            <input type="checkbox" checked={conCorreo} onChange={(e) => setConCorreo(e.target.checked)} /> Enviar por correo
          </label>
        )}
        {modo === 'respuesta' && (
          <label className="bz-check">
            y marcar como
            <select className="bz-select is-compacto" value={estadoFinal} onChange={(e) => setEstadoFinal(e.target.value)}>
              <option value="">(sin cambio)</option>
              <option value="en_espera">En espera</option>
              <option value="resuelto">Resuelto</option>
              <option value="cerrado">Cerrado</option>
            </select>
          </label>
        )}
        <Button className="ml-auto" onClick={enviar} disabled={!texto.trim() || enviando}>
          {enviando ? <Loader2 className="h-4 w-4 od-spin" /> : modo === 'nota' ? <Lock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
          {modo === 'nota' ? 'Guardar nota' : 'Enviar respuesta'}
        </Button>
      </div>
    </section>
  );
}

function DialogoReasignar({ persona, actual, onCancelar, onConfirmar }) {
  const [motivo, setMotivo] = useState('');
  const abierto = !!persona;
  const titulo = useMemo(() => (persona ? `Pasar el caso a ${persona.nombre}` : ''), [persona]);
  useEffect(() => { if (abierto) setMotivo(''); }, [abierto]);
  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCancelar()}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>
            {actual?.nombre} dejará de tener el caso. El motivo queda en el historial y le llega a {persona?.nombre} con el aviso.
          </DialogDescription>
        </DialogHeader>
        <textarea
          className="bz-textarea"
          rows={3}
          maxLength={300}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Motivo (opcional): «Es de su zona», «Estoy de vacaciones»…"
          aria-label="Motivo de la reasignación"
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={onCancelar}>Cancelar</Button>
          <Button onClick={() => onConfirmar(motivo.trim())}>Reasignar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
